/**
 * CreateOrEditSandboxConfigDialog — Dialog for creating or editing a sandbox configuration.
 *
 */

import { useState, useCallback, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  Divider,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tooltip,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Delete16Regular,
  BoxMultiple20Regular,
  Dismiss24Regular,
  PlugConnected20Regular,
  CheckmarkCircle16Filled,
  ErrorCircle16Filled,
} from '@fluentui/react-icons';
import { useSandboxConfigs, useSaveSandboxConfig } from '@/hooks/useSandboxConfigs';
import { useAppStore } from '@/stores/appStore';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import {
  useRepoOAuthConnection,
  getRepoOAuthStateMessage,
} from '@/hooks/useRepoOAuthConnection';
import { detectConnectorType } from '@/services/repoConnectionService';
import {
  SandboxRepository,
  SandboxConfiguration,
  RepositoryAuthType,
  RepoConnectorType,
  ResourceTier,
  ResourceTierLabels,
} from '@/types/sandbox';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '560px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  repoCard: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  repoHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  repoTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  dialogTitleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  oauthRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    flexWrap: 'wrap' as const,
  },
  oauthStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    minWidth: 0,
    flexWrap: 'wrap' as const,
  },
  oauthSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  oauthError: {
    color: tokens.colorPaletteRedForeground1,
  },
  oauthConnecting: {
    color: tokens.colorNeutralForeground2,
  },
});

// =============================================================================
// TYPES
// =============================================================================

interface RepoOAuthState {
  connectionId: string;
  connectorType: RepoConnectorType;
  automationProjectId: string;
  connected: boolean;
}

interface RepoFormState {
  id: string;
  url: string;
  branch: string;
  authType: RepositoryAuthType;
  staticToken?: string;
  oauth?: RepoOAuthState;
}

interface CreateOrEditSandboxConfigDialogProps {
  open: boolean;
  onCancel: () => void;
  /** When provided, the dialog operates in edit mode for this sandbox */
  sandboxConfig?: SandboxConfiguration;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CreateOrEditSandboxConfigDialog = ({ open, onCancel, sandboxConfig }: CreateOrEditSandboxConfigDialogProps) => {
  const styles = useStyles();
  const isEditMode = !!sandboxConfig;
  const { data: existingSandboxConfigs = [] } = useSandboxConfigs();
  const { mutateAsync: saveSandboxConfig, isPending: isSavingSandboxConfig, error: saveError, reset: resetSaveMutation } = useSaveSandboxConfig();
  const selectedProject = useAppStore((s) => s.selectedProject);
  const { project: currentProject } = useCurrentProject();

  // Extract the internal project GUID from the project's id field.
  // The data-plane returns id like "/automationProjects/ea7f4b09..." — we need the last segment.
  const automationProjectGuid = currentProject?.id?.split('/').pop() ?? '';

  const hasProject = !!(currentProject?.name || selectedProject);

  // OAuth connection hook
  const repoOAuth = useRepoOAuthConnection();
  // Track which repo index is currently doing OAuth
  const [oauthRepoIndex, setOauthRepoIndex] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [repos, setRepos] = useState<RepoFormState[]>([]);
  const [resourceTier, setResourceTier] = useState<ResourceTier>(ResourceTier.M);
  const [nameError, setNameError] = useState<string | null>(null);
  const [repoUrlTouched, setRepoUrlTouched] = useState<Set<number>>(new Set());
  const [initializedFor, setInitializedFor] = useState<string | null>(null);

  // Populate form when opening in edit mode
  useEffect(() => {
    if (!open) {
      setInitializedFor(null);
      return;
    }
    if (sandboxConfig && initializedFor !== sandboxConfig.name) {
      setName(sandboxConfig.name);
      setResourceTier(sandboxConfig.properties.resourceSettings?.resourceTier ?? ResourceTier.M);
      const existingRepos = sandboxConfig.properties.repositorySettings?.repositories ?? [];
      setRepos(
        existingRepos.map((r): RepoFormState => {
          const state: RepoFormState = {
            id: crypto.randomUUID(),
            url: r.url,
            branch: r.branch,
            authType: r.authType,
          };
          if (r.staticToken) {
            state.staticToken = r.staticToken;
          }
          if (r.authType === RepositoryAuthType.OAuth && r.connectionId) {
            state.oauth = {
              connectionId: r.connectionId,
              connectorType: r.connectorType ?? ('github' as RepoConnectorType),
              automationProjectId: r.automationProjectId || automationProjectGuid,
              connected: true,
            };
          }
          return state;
        }),
      );
      setNameError(null);
      setRepoUrlTouched(new Set());
      setInitializedFor(sandboxConfig.name);
    } else if (!sandboxConfig && initializedFor !== '__create__') {
      setRepos([{ id: crypto.randomUUID(), url: '', branch: '', authType: RepositoryAuthType.ManagedIdentity }]);
      setResourceTier(ResourceTier.M);
      setInitializedFor('__create__');
    }
  }, [open, sandboxConfig, initializedFor, automationProjectGuid]);

  // Validation
  const validateName = useCallback((value: string) => {
    if (!value) {
      setNameError('Name is required');
      return false;
    }
    if (value === 'default') {
      setNameError('"default" is reserved for internal use');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(value)) {
      setNameError('Lowercase letters, numbers, and hyphens only');
      return false;
    }
    if (existingSandboxConfigs.some((s) => s.name === value && (!isEditMode || s.name !== sandboxConfig?.name))) {
      setNameError('A sandbox configuration with this name already exists');
      return false;
    }
    setNameError(null);
    return true;
  }, [existingSandboxConfigs, isEditMode, sandboxConfig?.name]);

  // Close handler — cancels any in-progress OAuth and resets all form state
  const handleClose = useCallback(() => {
    if (repoOAuth.isInProgress) {
      repoOAuth.cancel();
    }
    repoOAuth.reset();
    resetSaveMutation();
    setOauthRepoIndex(null);
    setName('');
    setRepos([]);
    setResourceTier(ResourceTier.M);
    setNameError(null);
    setRepoUrlTouched(new Set());
    onCancel();
  }, [repoOAuth, resetSaveMutation, onCancel]);

  // Repository management
  const addRepo = useCallback(() => {
    setRepos((prev) => [...prev, { id: crypto.randomUUID(), url: '', branch: '', authType: RepositoryAuthType.ManagedIdentity }]);
  }, []);

  const removeRepo = useCallback((index: number) => {
    setRepos((prev) => prev.filter((_, i) => i !== index));
    // Remap repoUrlTouched indices: shift indices above the removed one down by 1
    setRepoUrlTouched((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      }
      return next;
    });
  }, []);

  const updateRepo = useCallback((index: number, field: keyof RepoFormState, value: string) => {
    setRepos((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        // Clear OAuth state when auth type changes away from OAuth
        if (field === 'authType' && value !== RepositoryAuthType.OAuth) {
          delete updated.oauth;
        }
        // Clear PAT when auth type changes away from PAT
        if (field === 'authType' && value !== RepositoryAuthType.PAT) {
          delete updated.staticToken;
        }
        // Clear OAuth state when URL changes (connection was for the old URL)
        if (field === 'url' && r.oauth) {
          delete updated.oauth;
        }
        return updated;
      }),
    );
  }, []);

  // OAuth connection management
  const handleOAuthConnect = useCallback(
    async (repoIndex: number) => {
      const repo = repos[repoIndex];
      if (!repo) return;

      // Detect connector type from URL
      const detectedType = detectConnectorType(repo.url);
      if (!detectedType) {
        return; // URL doesn't match known providers
      }

      if (!hasProject || !automationProjectGuid) {
        // OAuth connections require a project scope.
        // This shouldn't happen (button is disabled), but guard anyway.
        return;
      }

      setOauthRepoIndex(repoIndex);

      const result = await repoOAuth.connect({
        connectorType: detectedType,
        automationProjectId: automationProjectGuid,
      });

      if (result) {
        // Only mark as connected when the connection test succeeds.
        const isConnected = result.testResult?.success === true;

        // Update repo form state with OAuth result
        setRepos((prev) =>
          prev.map((r, i) =>
            i === repoIndex
              ? {
                  ...r,
                  oauth: {
                    connectionId: result.connectionId,
                    connectorType: result.connectorType,
                    automationProjectId: result.automationProjectId,
                    connected: isConnected,
                  },
                }
              : r,
          ),
        );
        // Only clear the active OAuth index on success so error
        // messages remain visible next to the correct repo card.
        if (isConnected) {
          setOauthRepoIndex(null);
        }
      }
      // On failure, keep oauthRepoIndex set so the error message renders.
    },
    [repos, repoOAuth, hasProject, automationProjectGuid],
  );

  // Submit
  const handleSubmit = useCallback(async () => {
    if (!validateName(name)) return;
    if (repos.length === 0) return;

    // Validate all repo URLs are filled
    const emptyUrlIndices = repos.reduce<number[]>((acc, r, i) => {
      if (!r.url.trim()) acc.push(i);
      return acc;
    }, []);
    if (emptyUrlIndices.length > 0) {
      setRepoUrlTouched(new Set(emptyUrlIndices));
      return;
    }

    // Validate OAuth repos have completed their consent flow
    const incompleteOAuth = repos.filter(
      (r) => r.url.trim() && r.authType === RepositoryAuthType.OAuth && !r.oauth?.connected,
    );
    if (incompleteOAuth.length > 0) {
      setNameError(null); // clear name error to avoid confusion
      return;
    }

    const repositories: SandboxRepository[] = repos
      .map((r) => {
        const base: SandboxRepository = {
          url: r.url.trim(),
          branch: r.branch,
          authType: r.authType,
        };

        // Include PAT when auth type is PAT
        if (r.authType === RepositoryAuthType.PAT && r.staticToken) {
          base.staticToken = r.staticToken;
        }

        // Include OAuth fields when auth type is OAuth.
        // automationProjectId must be the internal project GUID (not the name).
        if (r.authType === RepositoryAuthType.OAuth && r.oauth) {
          base.connectorType = r.oauth.connectorType;
          base.connectionId = r.oauth.connectionId;
          base.automationProjectId = automationProjectGuid || r.oauth.automationProjectId;
        }

        return base;
      });

    const result = await saveSandboxConfig({
      sandboxConfigName: name,
      request: {
        properties: {
          ...(repositories.length > 0 && {
            repositorySettings: { repositories },
          }),
          resourceSettings: { resourceTier },
        },
      },
    });

    if (result) {
      // Reset form and close
      setName('');
      setRepos([]);
      setRepoUrlTouched(new Set());
      repoOAuth.reset();
      setOauthRepoIndex(null);
      onCancel();
    }
  }, [name, repos, resourceTier, validateName, saveSandboxConfig, onCancel, repoOAuth, automationProjectGuid]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                aria-label="Close"
              />
            }
          >
            <span className={styles.dialogTitleContent}>
              <BoxMultiple20Regular />
              {isEditMode ? 'Edit sandbox configuration' : 'New sandbox configuration'}
            </span>
          </DialogTitle>

          <DialogContent className={styles.content}>
            {/* Name */}
            <Field
              label="Name"
              required
              validationState={nameError ? 'error' : 'none'}
              validationMessage={nameError}
              {...(!isEditMode ? { hint: 'Lowercase letters, numbers, and hyphens only' } : {})}
            >
              <Input
                value={name}
                onChange={(_, data) => {
                  setName(data.value);
                  if (nameError) validateName(data.value);
                }}
                disabled={isEditMode}
              />
            </Field>

            {/* Resource Tier */}
            <Field label="Resource Tier">
              <Dropdown
                value={ResourceTierLabels[resourceTier]}
                selectedOptions={[resourceTier]}
                onOptionSelect={(_, data) =>
                  setResourceTier((data.optionValue as ResourceTier) ?? ResourceTier.M)
                }
              >
                <Option value={ResourceTier.XS}>{ResourceTierLabels.XS}</Option>
                <Option value={ResourceTier.S}>{ResourceTierLabels.S}</Option>
                <Option value={ResourceTier.M}>{ResourceTierLabels.M}</Option>
                <Option value={ResourceTier.L}>{ResourceTierLabels.L}</Option>
              </Dropdown>
            </Field>

            <Divider />

            {/* Repositories */}
            <div className={styles.sectionHeader}>
              <Text weight="semibold">Repositories</Text>
              <Button
                appearance="secondary"
                size="small"
                icon={<Add16Regular />}
                onClick={addRepo}
              >
                Add repo
              </Button>
            </div>
            <Text className={styles.hint}>
              Repositories to clone into the sandbox. At least one repository is required.
            </Text>

            {repos.map((repo, ri) => (
              <div key={repo.id} className={styles.repoCard}>
                <div className={styles.repoHeader}>
                  <Text className={styles.repoTitle}>Repository {ri + 1}</Text>
                  <Tooltip
                    content="At least one repository is required"
                    relationship="label"
                    {...(repos.length > 1 && { visible: false })}
                  >
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Delete16Regular />}
                      onClick={() => removeRepo(ri)}
                      disabled={repos.length <= 1}
                      aria-label={`Remove repository ${ri + 1}`}
                    />
                  </Tooltip>
                </div>

                <Field
                  label="URL"
                  required
                  validationState={repoUrlTouched.has(ri) && !repo.url.trim() ? 'error' : 'none'}
                  validationMessage={repoUrlTouched.has(ri) && !repo.url.trim() ? 'Repository URL is required' : null}
                >
                  <Input
                    aria-label="URL"
                    size="small"
                    value={repo.url}
                    onChange={(_, data) => {
                      updateRepo(ri, 'url', data.value);
                      // Clear validation when user types
                      if (repoUrlTouched.has(ri) && data.value.trim()) {
                        setRepoUrlTouched((prev) => {
                          const next = new Set(prev);
                          next.delete(ri);
                          return next;
                        });
                      }
                    }}
                    placeholder="https://dev.azure.com/org/project/_git/repo"
                  />
                </Field>

                <div className={styles.twoCol}>
                  <Field label="Branch">
                    <Input
                      aria-label="Branch"
                      size="small"
                      placeholder="e.g. main"
                      value={repo.branch}
                      onChange={(_, data) => updateRepo(ri, 'branch', data.value)}
                    />
                  </Field>
                  <Field label="Auth type">
                    <Dropdown
                      size="small"
                      value={repo.authType}
                      selectedOptions={[repo.authType]}
                      onOptionSelect={(_, data) =>
                        updateRepo(ri, 'authType', data.optionValue ?? RepositoryAuthType.ManagedIdentity)
                      }
                    >
                      <Option value={RepositoryAuthType.ManagedIdentity}>Managed Identity</Option>
                      <Option value={RepositoryAuthType.PAT}>PAT</Option>
                      <Option value={RepositoryAuthType.OAuth}>OAuth</Option>
                    </Dropdown>
                  </Field>
                </div>

                {/* PAT input field */}
                {repo.authType === RepositoryAuthType.PAT && (
                  <Field label="Personal Access Token" required>
                    <Input
                      size="small"
                      type="password"
                      value={repo.staticToken ?? ''}
                      onChange={(_, data) => updateRepo(ri, 'staticToken', data.value)}
                      placeholder="Enter your PAT"
                    />
                  </Field>
                )}

                {/* OAuth connection UI */}
                {repo.authType === RepositoryAuthType.OAuth && (
                  <div className={styles.oauthRow}>
                    {repo.oauth?.connected ? (
                      <div className={`${styles.oauthStatus} ${styles.oauthSuccess}`}>
                        <CheckmarkCircle16Filled />
                        <Text size={200}>
                          Connected via {repo.oauth.connectorType === 'azuredevops' ? 'Azure DevOps' : 'GitHub'}
                        </Text>
                        <Badge appearance="ghost" size="small">
                          {repo.oauth.connectionId.split('/').pop()}
                        </Badge>
                      </div>
                    ) : oauthRepoIndex === ri && repoOAuth.isInProgress ? (
                      <div className={`${styles.oauthStatus} ${styles.oauthConnecting}`}>
                        <Spinner size="tiny" />
                        <Text size={200}>{getRepoOAuthStateMessage(repoOAuth.state)}</Text>
                      </div>
                    ) : (
                      <>
                        <Button
                          appearance="primary"
                          size="small"
                          icon={<PlugConnected20Regular />}
                          onClick={() => handleOAuthConnect(ri)}
                          disabled={
                            !repo.url.trim() ||
                            !detectConnectorType(repo.url) ||
                            !hasProject ||
                            !automationProjectGuid ||
                            repoOAuth.isInProgress
                          }
                        >
                          {detectConnectorType(repo.url) === 'azuredevops'
                            ? 'Connect Azure DevOps'
                            : detectConnectorType(repo.url) === 'github'
                              ? 'Connect GitHub'
                              : 'Connect'}
                        </Button>
                        {oauthRepoIndex === ri && repoOAuth.error && (
                          <div className={`${styles.oauthStatus} ${styles.oauthError}`}>
                            <ErrorCircle16Filled />
                            <Text size={200}>{repoOAuth.error}</Text>
                          </div>
                        )}
                        {repo.url.trim() && !detectConnectorType(repo.url) && (
                          <Text className={styles.hint}>
                            Enter an Azure DevOps or GitHub URL to enable OAuth
                          </Text>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </DialogContent>

          {saveError && (
            <MessageBar intent="error">
              <MessageBarBody>
                {saveError.message || 'Failed to save sandbox configuration.'}
              </MessageBarBody>
            </MessageBar>
          )}

          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={
                isSavingSandboxConfig ||
                !name ||
                repos.length === 0 ||
                repoOAuth.isInProgress ||
                repos.some((r) => !r.url.trim()) ||
                repos.some((r) => r.url.trim() && r.authType === RepositoryAuthType.PAT && !r.staticToken?.trim()) ||
                repos.some((r) => r.url.trim() && r.authType === RepositoryAuthType.OAuth && !r.oauth?.connected)
              }
            >
              {isSavingSandboxConfig
                ? (isEditMode ? 'Saving…' : 'Creating…')
                : (isEditMode ? 'Save' : 'Create')
              }
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

CreateOrEditSandboxConfigDialog.displayName = 'CreateOrEditSandboxConfigDialog';
